import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Card,
  Col,
  FormGroup,
  Label,
  Row,
  Table,
  TabPane,
  UncontrolledTooltip,
} from "reactstrap";
import { getJobQuotationHistory } from "actions/jobQuotation";
import { formatSAR } from "components/Common/commonFunctions";
import { useDispatch, useSelector } from "react-redux";
import { quotationHistoryList } from "constants/quotation";
import { getPaymentDetails } from "actions/job";
import { jobPaymentDetails } from "constants/job";
import { PAYMENT_METHODS, JOB_STATUS } from "components/Common/constants";
import { getQuotationList } from "actions/quotation";

const QuotationAndPayments = ({
  showDetails,
  setShowDetails,
  handleFileDownload,
  renderQuotationActionBtns,
  decodedId,
  selectedParts,
  selectedServices,
  quotationSummary,
  quotationLoading,
  quotationError,
}) => {
  const notificationAlertRef = useRef(null);
  const dispatch = useDispatch();

  const [showAlternative, setShowAlternative] = useState({
    loader: true,
    error: "",
    isPayementLoading: true,
    paymentError: "",
  });

  const quotationHistory = useSelector(
    (state) => state.quotation.quotationHistoryList
  );
  const quotationList = useSelector((state) => state.quotation.quotationList);
  const paymentDetails = useSelector((state) => state.job.jobPaymentDetails);

  const quotationData = quotationHistory?.data?.quotations;

  // Fetch everything on mount
  useEffect(() => {
    if (!decodedId) return;

    setShowAlternative((prev) => ({ ...prev, loader: true, error: "" }));

    getJobQuotationHistory(dispatch, decodedId);
    getPaymentDetails(dispatch, decodedId);
    getQuotationList(dispatch, decodedId);

    return () => {
      dispatch({ type: quotationHistoryList, payload: {} });
      dispatch({ type: jobPaymentDetails, payload: {} });
    };
  }, [dispatch, decodedId]);

  // ---- Resolve the active quotation summary ----
  // Prefer the prop (from parent), fall back to the Redux store
  const summary = useMemo(() => {
    if (quotationSummary && (quotationSummary.ID || quotationSummary.grandTotal != null)) {
      return quotationSummary;
    }
    if (quotationList?.code === 200 && quotationList?.data) {
      return quotationList.data;
    }
    return null;
  }, [quotationSummary, quotationList]);

  // ---- Build quotation items list ----
  // Prefer prop arrays (selectedParts/selectedServices), fall back to store
  const quotationItems = useMemo(() => {
    const fromProps = [...(selectedParts || []), ...(selectedServices || [])];
    if (fromProps.length > 0) return fromProps;

    const parts = summary?.quotationItems?.spareParts || [];
    const services = summary?.quotationItems?.services || [];
    return [...parts, ...services];
  }, [selectedParts, selectedServices, summary]);

  const validQuotationItems = useMemo(
    () => quotationItems.filter((item) => (item.quantity || 0) > 0),
    [quotationItems]
  );

  // ---- Loading / error state for quotation list ----
  useEffect(() => {
    // Parent supplies its own loading/error; respect those first
    if (quotationLoading) {
      setShowAlternative((prev) => ({ ...prev, loader: true }));
      return;
    }
    if (quotationError) {
      setShowAlternative((prev) => ({
        ...prev,
        loader: false,
        error: quotationError,
      }));
      return;
    }

    if (!quotationList) return;

    if (quotationList.code === 200) {
      setShowAlternative((prev) => ({ ...prev, loader: false, error: "" }));
    } else if (quotationList.code || quotationList.message) {
      setShowAlternative((prev) => ({
        ...prev,
        loader: false,
        error:
          quotationList?.message ||
          "Something went wrong loading quotation details",
      }));
    }
  }, [quotationList, quotationLoading, quotationError]);

  // ---- History loading ----
  useEffect(() => {
    if (quotationHistory?.code === 200) {
      setShowAlternative((prev) => ({ ...prev, loader: false, error: "" }));
    } else if (quotationHistory?.code || quotationHistory?.message) {
      setShowAlternative((prev) => ({
        ...prev,
        loader: false,
        error:
          quotationHistory?.message ||
          "Something went wrong loading quotation details",
      }));
    }
  }, [quotationHistory]);

  // ---- Payment loading ----
  useEffect(() => {
    if (paymentDetails?.code === 200) {
      setShowAlternative((prev) => ({
        ...prev,
        isPayementLoading: false,
        paymentError: "",
      }));
    } else if (paymentDetails?.code || paymentDetails?.message) {
      setShowAlternative((prev) => ({
        ...prev,
        isPayementLoading: false,
        paymentError:
          paymentDetails?.message ||
          "Something went wrong, failed to loading payment details",
      }));
    }
  }, [paymentDetails]);

  // const getQuotationHistoryDetails = async (id) => {
  //   try {
  //     setShowAlternative({ loader: true, error: false });
  //     const res = await getJobQuotationHistory(id);
  //     if (res.code === 200) {
  //       setQuotationData(res?.data?.quotations || []);
  //     } else {
  //       setShowAlternative({ loader: false, error: "" });
  //       const message = Array.isArray(res?.message)
  //         ? res.message
  //           .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
  //           .join(", ")
  //         : res?.message || "Failed to load quotation history  details!";
  //       notify("danger", message);
  //     }
  //   } catch (error) {
  //     setShowAlternative({
  //       loader: false,
  //       error: "Something went wrong loading quotation details",
  //     });
  //   }
  // };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const paymentData = paymentDetails?.data;

  const successfulPayments =
    paymentData?.payment?.filter(
      (payment) => payment?.paymentStatus === "SUCCESS"
    ) || [];

  const paymentReceivedList =
    paymentData?.isPaymentReceived?.filter(
      (item) => item?.approvalNote || item?.isPaymentReceived
    ) || [];

  const hasBankTransfer = !!paymentData?.recordBankTransfer?.trim();

  const showNoPaymentDetails =
    successfulPayments.length === 0 &&
    !hasBankTransfer &&
    paymentReceivedList.length === 0;

  // Helper: format number safely
  const safeFormat = (value) =>
    value == null ? "0" : formatSAR(value);

  return (
    <TabPane>
      <Row>
        <Col md="12">
          <Card className="rounded border-0 mb-4 px-4 py-3 custom-card">
            <div className="d-flex align-items-center mb-1">
              <h4 className="text-default">
                Quotation {summary?.ID && ` ID: ${summary.ID}`}
              </h4>
            </div>
            <div className="border-bottom border-muted mb-3" />
            <Row>
              <Col>
                <div className="quotation-container">
                  {renderQuotationActionBtns?.()}

                  <Table borderless className="custom-table">
                    <thead>
                      <tr>
                        <th className="col-item">Item Number</th>
                        <th className="col-description">Description</th>
                        <th className="col-qty text-right">Quantity</th>
                        <th className="col-unit-price text-right">
                          Unit Price
                        </th>
                        <th className="col-line-total text-right">
                          Item Total
                        </th>
                        <th className="col-discount text-right">Discount</th>
                        <th className="col-amount text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showAlternative.loader ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <i className="fa fa-spinner fa-spin fa-2x text-muted" />
                          </td>
                        </tr>
                      ) : showAlternative.error ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="text-center text-danger py-4"
                          >
                            {showAlternative.error}
                          </td>
                        </tr>
                      ) : summary?.status === "INACTIVE" ? (
                        <tr>
                          <td colSpan="7" className="text-center text-muted">
                            No quotation items available
                          </td>
                        </tr>
                      ) : validQuotationItems?.length > 0 ? (
                        validQuotationItems.map((item, index) => {
                          const qty = item.quantity || 0;
                          const unitPrice = item.unitPrice || 0;
                          const lineTotal = (qty * unitPrice).toFixed(2);
                          const total =
                            item.totalPrice ??
                            item.discountedTotalPrice ??
                            lineTotal;

                          return (
                            <tr key={item.ID || item.itemID || index}>
                              <td className="col-item">
                                {item.itemNumber || "-"}
                              </td>
                              <td className="col-description">
                                {item.description || item.title || "-"}
                              </td>
                              <td className="col-qty text-right">{qty}</td>
                              <td className="col-unit-price text-right">
                                SAR {safeFormat(unitPrice)}
                              </td>
                              <td className="col-line-total text-right">
                                SAR {safeFormat(lineTotal)}
                              </td>
                              <td className="col-discount text-right">
                                {item.discount
                                  ? `${Number(item.discount).toFixed(2)}%`
                                  : "0.00%"}
                              </td>
                              <td className="col-amount text-right">
                                SAR {safeFormat(total)}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center text-muted">
                            No quotation items available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {showDetails && (
                    <div className="quotation-details-card shadow-sm rounded mb-2 py-2 open">
                      <div className="quotation-details-row d-flex flex-wrap w-100">
                        <div className="quotation-left flex-grow-1"></div>
                        <div className="quotation-right flex-grow-1 px-3 py-2">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">
                              Spare Parts Charges
                            </span>
                            <span className="text-dark">
                              SAR {safeFormat(summary?.sparePartTotal)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">Service Charges</span>
                            <span className="text-dark">
                              SAR {safeFormat(summary?.serviceTotal)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">SubTotal</span>
                            <span className="text-dark">
                              SAR {safeFormat(summary?.subtotal)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">
                              Discount (
                              {Number(summary?.discountPercentage ?? 0).toFixed(
                                2
                              )}
                              %)
                            </span>
                            <span className="text-dark">
                              SAR {safeFormat(summary?.discount)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">Discounted Total</span>
                            <span className="text-dark">
                              SAR {safeFormat(summary?.discountedTotal)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">VAT 15.00 %</span>
                            <span className="text-dark">
                              SAR {safeFormat(summary?.vatAmount)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="quotation-total-bar shadow-sm rounded">
                    <div className="quotation-total-row d-flex flex-wrap w-100">
                      <div className="quotation-left flex-grow-1"></div>
                      <div className="quotation-right flex-grow-1 px-3 py-2">
                        <div className="d-flex justify-content-between mb-1">
                          <h6
                            className="mb-0 text-muted text-uppercase pointer d-flex align-items-center"
                            onClick={() => setShowDetails(!showDetails)}
                          >
                            Grand Total
                            <i
                              className={`fa fa-chevron-${showDetails ? "up" : "down"
                                } text-info ml-2`}
                            />
                          </h6>
                          <h4 className="mb-0 text-dark">
                            SAR {safeFormat(summary?.grandTotal)}
                          </h4>
                        </div>

                        {summary?.clientPaymentType === "CASH" &&
                          summary?.paidAmount != null && (
                            <>
                              <div className="d-flex justify-content-between mb-1">
                                <h6 className="mb-0 text-muted text-uppercase">
                                  Paid Amount
                                </h6>
                                <h4 className="mb-0 text-dark">
                                  SAR {safeFormat(summary?.paidAmount)}
                                </h4>
                              </div>
                              <div className="d-flex justify-content-between mb-1">
                                <h6 className="mb-0 text-muted text-uppercase">
                                  Payable Amount
                                </h6>
                                <h4 className="mb-0 text-dark">
                                  SAR {safeFormat(summary?.payableAmount)}
                                </h4>
                              </div>
                            </>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
          <Row>
            <Col md="6">
              <Card className="rounded mb-4 px-4 py-3 custom-card">
                <Row className="align-items-center mb-2">
                  <Col>
                    <h4 className="text-default mb-0">Payment Details</h4>
                  </Col>
                </Row>

                <div className="border-bottom border-muted mb-3" />

                <div>
                  {showAlternative.isPayementLoading ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl" />
                    </div>
                  ) : (
                    <>
                      {/* Successful Payments */}
                      {successfulPayments.map((payment, index) => (
                        <React.Fragment key={index}>
                          <Row>
                            <Col xs="12" md="6" className="mb-1">
                              <FormGroup className="d-flex flex-column align-items-start">
                                <Label>Quotation ID</Label>
                                <span className="small-text">
                                  {payment?.quotationID || "-"}
                                </span>
                              </FormGroup>
                            </Col>

                            <Col xs="12" md="6" className="mb-1">
                              <FormGroup className="d-flex flex-column align-items-start">
                                <Label>Payment Method</Label>
                                <span className="small-text">
                                  {PAYMENT_METHODS[payment?.paymentType]
                                    ?.value || "-"}
                                </span>
                              </FormGroup>
                            </Col>

                            <Col xs="12" md="6" className="mb-1">
                              <FormGroup className="d-flex flex-column align-items-start">
                                <Label>Card Type</Label>
                                <span className="small-text">
                                  {payment?.paymentMethod || "-"}
                                </span>
                              </FormGroup>
                            </Col>

                            <Col xs="12" md="6" className="mb-1">
                              <FormGroup className="d-flex flex-column align-items-start">
                                <Label>Hyperpay Transaction ID</Label>
                                <span className="small-text">
                                  {payment?.pgPaymentID || "-"}
                                </span>
                              </FormGroup>
                            </Col>

                            <Col xs="12" md="6" className="mb-1">
                              <FormGroup className="d-flex flex-column align-items-start">
                                <Label>Transaction ID</Label>
                                <span className="small-text">
                                  {payment?.merchantTransactionID || "-"}
                                </span>
                              </FormGroup>
                            </Col>

                            <Col xs="12" md="6" className="mb-1">
                              <FormGroup className="d-flex flex-column align-items-start">
                                <Label>Payment Date</Label>
                                <span className="small-text">
                                  {payment?.paymentResponseReceivedAt || "-"}
                                </span>
                              </FormGroup>
                            </Col>

                            <Col xs="12" md="6" className="mb-1">
                              <FormGroup className="d-flex flex-column align-items-start">
                                <Label>Transaction Amount</Label>
                                <span className="small-text">
                                  {payment?.currency || "SAR"}{" "}
                                  {payment?.transactionAmount || "-"}
                                </span>
                              </FormGroup>
                            </Col>
                          </Row>

                          {index !== successfulPayments.length - 1 && (
                            <hr className="mt-0 mb-4" />
                          )}
                        </React.Fragment>
                      ))}

                      {/* Bank Transfer Note */}
                      {hasBankTransfer && (
                        <>
                          {successfulPayments.length > 0 && (
                            <hr className="mt-0 mb-4" />
                          )}

                          <Row>
                            <Col xs="12" md="12" className="mb-3">
                              <FormGroup className="d-flex flex-column align-items-start">
                                <Label>Payment Confirmation Note</Label>
                                <span className="small-text">
                                  {paymentData.recordBankTransfer}
                                </span>
                              </FormGroup>
                            </Col>
                          </Row>
                        </>
                      )}

                      {/* Payment Received */}
                      {paymentReceivedList.length > 0 && (
                        <>
                          {(successfulPayments.length > 0 ||
                            hasBankTransfer) && <hr className="mt-0 mb-4" />}

                          {paymentReceivedList.map(
                            (paymentReceived, index) => (
                              <React.Fragment key={index}>
                                <Row>
                                  <Col xs="12" md="6" className="mb-1">
                                    <FormGroup className="d-flex flex-column align-items-start">
                                      <Label>Quotation ID</Label>
                                      <span className="small-text">
                                        {paymentReceived?.quotationID || "-"}
                                      </span>
                                    </FormGroup>
                                  </Col>

                                  <Col xs="12" md="6" className="mb-1">
                                    <FormGroup className="d-flex flex-column align-items-start">
                                      <Label>Is Payment Received</Label>
                                      <span className="small-text">
                                        {paymentReceived?.isPaymentReceived ||
                                          "-"}
                                      </span>
                                    </FormGroup>
                                  </Col>

                                  <Col xs="12" md="12" className="mb-1">
                                    <FormGroup className="d-flex flex-column align-items-start">
                                      <Label>Received Note</Label>
                                      <span className="small-text">
                                        {paymentReceived?.approvalNote || "-"}
                                      </span>
                                    </FormGroup>
                                  </Col>
                                </Row>

                                {index !== paymentReceivedList.length - 1 && (
                                  <hr className="mt-0 mb-4" />
                                )}
                              </React.Fragment>
                            )
                          )}
                        </>
                      )}

                      {/* Error / Empty State */}
                      {showAlternative.paymentError ? (
                        <div className="text-center text-muted py-4">
                          {showAlternative.paymentError}
                        </div>
                      ) : (
                        showNoPaymentDetails && (
                          <div className="text-center text-muted py-4">
                            No payment details available
                          </div>
                        )
                      )}
                    </>
                  )}
                </div>
              </Card>
            </Col>
            <Col md="6">
              <Card className="rounded border-0 mb-4 p-4 shadow-sm custom-card quotation-box">
                <h4 className="mb-3 text-default">Quotation History</h4>
                <div className="border-bottom border-muted mb-3" />

                <div className="custom-timeline">
                  {quotationData && quotationData.length > 0 ? (
                    quotationData.map((item) => {
                      const formattedDate = item.createdAt
                        ? (() => {
                          const d = new Date(item.createdAt);
                          return isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
                        })()
                        : "-";

                      const statusLabel =
                        JOB_STATUS?.[item?.quotationStatus]?.label || "-";
                      const rejectionReason = item?.rejectionReason || "-";

                      return (
                        <div
                          key={item.quotationID ?? item.createdAt}
                          className="hover-lift rounded p-3 mb-3 d-flex flex-wrap align-items-center"
                        >
                          {/* Quotation ID */}
                          <div className="col-6 col-sm-3 col-md-2 mb-2 mb-md-0">
                            <span className="small text-muted d-block mb-1">Quotation ID</span>
                            <span className="small-text text-truncate d-block" title={item.quotationID || "-"}>
                              {item.quotationID || "-"}
                            </span>
                          </div>

                          {/* Date */}
                          <div className="col-6 col-sm-3 col-md-2 mb-2 mb-md-0">
                            <span className="small text-muted d-block mb-1">Date</span>
                            <span className="small-text text-truncate d-block" title={formattedDate}>
                              {formattedDate}
                            </span>
                          </div>

                          {/* Status + Rejection Reason — same line, equal width */}
                          <div className="col-12 col-sm-4 col-md-6 mb-2 mb-md-0">
                            <div className="row g-0">
                              <div className="col-6 pe-2">
                                <span className="small text-muted d-block mb-1">Status</span>
                                <span
                                  className="small-text text-truncate d-block"
                                  title={statusLabel}
                                >
                                  {statusLabel}
                                </span>
                              </div>
                              <div className="col-6">
                                <span className="small text-muted d-block mb-1">Rejection Reason</span>
                                <span
                                  className="small-text text-truncate d-block"
                                  title={rejectionReason}
                                >
                                  {rejectionReason}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Download action */}
                          <div className="col-12 col-sm-2 col-md-2 mt-2 mt-sm-0 text-sm-end text-start">
                            <button
                              type="button"
                              id={`download-quotation-${item.quotationID ?? item.createdAt}`}
                              className="btn btn-link p-0 text-default"
                              style={{ color: "inherit", textDecoration: "none" }}
                              onClick={() => handleFileDownload("quotation", item)}
                              aria-label={`Download quotation ${item.quotationID || ""}`}
                            >
                              <i className="fa fa-download" />
                            </button>
                            <UncontrolledTooltip
                              delay={0}
                              placement="top"
                              target={`download-quotation-${item.quotationID ?? item.createdAt}`}
                            >
                              Download
                            </UncontrolledTooltip>
                          </div>
                        </div>
                      );
                    })
                  ) : showAlternative.error ? (
                    <div className="text-center text-muted py-4">{showAlternative.error}</div>
                  ) : (
                    <div className="text-center text-muted py-4">
                      No quotation history available
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </TabPane>
  );
};

export default QuotationAndPayments;
